import { Head, Link } from '@inertiajs/react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import type { Breadcrumb } from '@/types/shared'

type PlayerSummary = {
  id: string
  name: string
  teamName?: string | null
  prefecture?: string | null
  positions: string[]
  height?: number | null
  profileStatus: string
  createdAt: string
}

type AdminPlayersIndexProps = {
  players: PlayerSummary[]
  breadcrumbs: Breadcrumb[]
}

const statusLabels: Record<string, string> = {
  approved: '承認済み',
  pending: '承認待ち',
  nothing: '未登録',
}

const statusClasses: Record<string, string> = {
  approved: 'bg-success-400/20 text-success-200 border border-success-400/30',
  pending: 'bg-warning-400/20 text-warning-200 border border-warning-400/30',
  nothing: 'bg-white/10 text-white/70 border border-white/10',
}

export default function AdminPlayersIndex({ players, breadcrumbs }: AdminPlayersIndexProps) {
  return (
    <AppShell>
      <Head title="選手一覧" />
      <PageHeader
        title="選手一覧"
        description="登録済みのプレイヤー情報を確認・管理できます。"
        breadcrumbs={breadcrumbs}
      />

      <Card padding="none" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/5 text-sm text-white/80">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-white/60">
              <tr>
                <th className="px-6 py-4 text-left font-semibold">選手名</th>
                <th className="px-6 py-4 text-left font-semibold">チーム</th>
                <th className="px-6 py-4 text-left font-semibold">都道府県</th>
                <th className="px-6 py-4 text-left font-semibold">ポジション</th>
                <th className="px-6 py-4 text-left font-semibold">身長</th>
                <th className="px-6 py-4 text-left font-semibold">プロフィール</th>
                <th className="px-6 py-4 text-left font-semibold">登録日</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {players.map((player) => (
                <tr key={player.id} className="hover:bg-white/5 transition">
                  <td className="px-6 py-4 font-medium text-white">{player.name}</td>
                  <td className="px-6 py-4 text-white/70">{player.teamName ?? '未設定'}</td>
                  <td className="px-6 py-4 text-white/70">{player.prefecture ?? '未設定'}</td>
                  <td className="px-6 py-4 text-white/70">
                    {player.positions.length ? player.positions.join(', ') : '未設定'}
                  </td>
                  <td className="px-6 py-4 text-white/70">
                    {player.height != null ? `${Number(player.height)} cm` : '未設定'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs ${statusClasses[player.profileStatus] ?? statusClasses.nothing}`}>
                      {statusLabels[player.profileStatus] ?? statusLabels.nothing}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-white/60">{formatDate(player.createdAt)}</td>
                  <td className="px-6 py-4 text-right">
                    <Link href={`/admin/players/${player.id}`} className="text-brand-200 hover:text-brand-100">
                      詳細
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {players.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-white/50">登録されている選手がいません。</div>
        ) : null}
      </Card>
    </AppShell>
  )
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('ja-JP')
}
